import { useFlag } from '@calendly/feature-check-react'
import { useMemo } from 'react'

import { useEventType } from 'features/event_type_provider'

import { useFetchLegacyWorkflowsForEventTypeQuery, useFetchWorkflowsForEventTypeQuery } from '../../../api'
import { useEventTypeEditorSidebar } from '../../../hooks'

const getWorkflowsOwner = ({ ownerType, ownerId, creatorId }) => {
  if (ownerType === 'Organization') {
    return { id: creatorId, type: 'user' }
  }
  return { id: ownerId, type: ownerType }
}

export const useWorkflows = ({ withAdminManaged = false } = {}) => {
  const workflowsExtractionEnabled = useFlag('raven_workflows_extraction')

  const { id: eventTypeId, creator_id: creatorId, isMETParent, parent_id: parentId } = useEventType()
  const { type, ownerType, ownerId } = useEventTypeEditorSidebar()
  const { id: workflowOwnerId, type: workflowOwnerType } = getWorkflowsOwner({ ownerType, ownerId, creatorId })

  const queryParams = useMemo(() => {
    // For managed event types,
    //   we need to ensure the query runs even without traditional creator/owner setup
    //   and only include owner info if available
    if (isMETParent) {
      return {
        eventTypeId,
        isMETParent,
        parentId,
        type,
        ...(workflowOwnerId && {
          ownerId: workflowOwnerId,
          ownerType: workflowOwnerType
        }),
        withAdminManaged
      }
    }

    return {
      eventTypeId,
      isMETParent,
      parentId,
      type,
      ownerId: workflowOwnerId,
      ownerType: workflowOwnerType,
      withAdminManaged
    }
  }, [
    eventTypeId,
    isMETParent,
    parentId,
    type,
    workflowOwnerId,
    workflowOwnerType,
    withAdminManaged
  ])

  const { data: workflowsData } = useFetchWorkflowsForEventTypeQuery(queryParams, {
    skip: !workflowsExtractionEnabled || !queryParams,
    refetchOnMountOrArgChange: true
  })
  const { data: legacyWorkflowsData } = useFetchLegacyWorkflowsForEventTypeQuery(queryParams, {
    skip: workflowsExtractionEnabled || !queryParams,
    refetchOnMountOrArgChange: true
  })

  return useMemo(() => (
    workflowsData || legacyWorkflowsData || { list: [], canManageWorkflows: false }
  ), [workflowsData, legacyWorkflowsData])
}
